import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLabel } from '@platform/hooks/useLabel';
import { useDeleteCustomModuleMutation, useGetCatalogQuery, useMeQuery, useUpdateWorkspaceModuleMutation } from '@platform/state/api';
import { useAppDispatch } from '@platform/state/hooks';
import { toastShown } from '@platform/state/slices/uiSlice';
import type { IModuleRemoval } from './IModuleRemoval';
import { saveErrorText } from './saveErrorText';

/**
 * «Видалити модуль» конструктора (прототип v4 bCanDelete = !core; ADR-025): власний модуль — DELETE …/custom-modules/{key} разом із записами;
 * модуль каталогу, що не є основним чи платформним, — вимкнення в просторі (PATCH …/modules/{key} { enabled: false }: зникає з меню, записи
 * зберігаються, повертається в «Встановлених модулях»); основний, уже вимкнений чи без права customize — недоступно. Підтвердження з
 * назвою модуля (UIUX patterns/confirmations) — на кнопці; remove повертає, чи вдалося.
 */
export function useModuleRemoval(ws: string, moduleKey: string): IModuleRemoval {
  const { t } = useTranslation();
  const label = useLabel();
  const dispatch = useAppDispatch();
  const { data: me } = useMeQuery();
  const { data: catalog } = useGetCatalogQuery({ workspaceId: ws }, { skip: !ws });
  const [deleteCustom] = useDeleteCustomModuleMutation();
  const [updateModule] = useUpdateWorkspaceModuleMutation();
  const summary = catalog?.modules.find((m) => m.key === moduleKey);
  const custom = summary?.kind === 'custom';
  const disabled = !!(summary as { disabled?: boolean } | undefined)?.disabled;
  const canEdit = !!me?.workspaces.find((w) => w.id === ws)?.myCapabilities.includes('customize');
  const available = !!summary && canEdit && (custom || (!summary.core && !summary.platformCapability && summary.installed && !disabled));
  const remove = useCallback(async () => {
    try {
      if (custom) await deleteCustom({ ws, key: moduleKey }).unwrap();
      else await updateModule({ ws, moduleKey, body: { enabled: false } }).unwrap();
      return true;
    } catch (e) {
      dispatch(toastShown({ kind: 'error', text: saveErrorText(e, t('rtBuilder.conflict'), t('rtBuilder.saveFailed')) }));
      return false;
    }
  }, [custom, deleteCustom, updateModule, ws, moduleKey, dispatch, t]);
  return { available, custom, name: label(summary?.name) || moduleKey, remove };
}
